import {JobNodes, makeJobTaskDescriptor} from "#job-nodes/job-nodes"
import type {TextureType} from "@cm/types"
import DataObjectReference = JobNodes.DataObjectReference

// for simplicity this should match DownloadResolution in GQL
export type ThumbnailResolution =
    | "Tiny" // 500px
    | "Low" // 1000px
    | "Medium" // 2000px
    | "High" // original resolution

export type ThumbnailFormat = "jpg" | "tiff"

export type ThumbnailOptions = {
    generateTiles?: boolean
    generateThumbnails?: [
        {
            resolution: ThumbnailResolution
            formats: ThumbnailFormat[]
        },
    ]
    apply_pbr_specific_corrections?: boolean // defaults to false
}

export type GenerateThumbnailsDataObjectInput = {
    dataObject: DataObjectReference
    options?: ThumbnailOptions
}

export type GenerateThumbnailsDataObjectOutput = {
    dataObject: DataObjectReference
}

export const generateThumbnailsDataObjectTask = makeJobTaskDescriptor<GenerateThumbnailsDataObjectInput, GenerateThumbnailsDataObjectOutput>(
    "GenerateThumbnails.DataObject",
    1,
)

/**
 * Builds the job graph that generates thumbnails for a single data object. Shared
 * by the GraphQL create mutation and the coordinator's thumbnail-on-complete step
 * so the task wiring (the part most likely to drift) lives in one place.
 */
export function buildGenerateThumbnailsDataObjectGraph(input: {dataObjectId: number | string; options?: ThumbnailOptions; platformVersion?: string}) {
    return JobNodes.jobGraph(
        JobNodes.task(generateThumbnailsDataObjectTask, {
            input: JobNodes.value({
                dataObject: JobNodes.dataObjectReference(input.dataObjectId),
                options: input.options,
            }),
        }),
        {platformVersion: input.platformVersion},
    )
}

//------------------------------------------------------------

export type TypedDataObject = {
    textureType: TextureType
    dataObject: DataObjectReference
}

export type TypedDataObjectSet = TypedDataObject[]

export type GenerateThumbnailsTypedDataObjectSetInput = {
    typedDataObjectSet: TypedDataObjectSet
    options?: ThumbnailOptions
}

export type GenerateThumbnailsTypedDataObjectSetOutput = {
    typedDataObjectSet: TypedDataObjectSet
}

export const generateThumbnailsTypedDataObjectSetTask = makeJobTaskDescriptor<
    GenerateThumbnailsTypedDataObjectSetInput,
    GenerateThumbnailsTypedDataObjectSetOutput
>("GenerateThumbnails.TypedDataObjectSet", 1)

//------------------------------------------------------------

export type GenerateThumbnailsTextureSetInput = {
    textureSetRevision: JobNodes.TextureSetRevisionReference
    options?: ThumbnailOptions
}

export type GenerateThumbnailsTextureSetOutput = {
    textureSetRevision: JobNodes.TextureSetRevisionReference
}

export const generateThumbnailsTextureSetTask = makeJobTaskDescriptor<GenerateThumbnailsTextureSetInput, GenerateThumbnailsTextureSetOutput>(
    "GenerateThumbnails.TextureSetRevision",
    1,
)
